import {
  ExternalLink,
  FileText,
  Headphones,
  Map as MapIcon,
  Plus,
  Trash2,
  Video,
} from "lucide-react";
import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  type MediaLinkRow,
  type MediaType,
  useCreateMedia,
  useDeleteMedia,
  useMedia,
  useUpdateMedia,
} from "@/hooks/use-media";
import { cn } from "@/lib/utils";
import { resolveEmbed } from "./media-embeds";

const TYPE_OPTIONS: Array<{ value: MediaType; label: string; Icon: typeof Headphones }> = [
  { value: "audio", label: "Audio", Icon: Headphones },
  { value: "video", label: "Video", Icon: Video },
  { value: "mind_map", label: "Mind map", Icon: MapIcon },
  { value: "document", label: "Document", Icon: FileText },
  { value: "other", label: "Other", Icon: ExternalLink },
];

function iconFor(type: MediaType) {
  return TYPE_OPTIONS.find((o) => o.value === type)?.Icon ?? ExternalLink;
}

function MediaEmbed({ link }: { link: MediaLinkRow }) {
  const { kind, src } = resolveEmbed(link.url, link.type);

  if (kind === "youtube" || kind === "vimeo" || kind === "loom") {
    return (
      <div className="aspect-video rounded-md overflow-hidden border bg-muted/40">
        <iframe
          title={link.label}
          src={src}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="w-full h-full"
        />
      </div>
    );
  }
  if (kind === "audio-direct") {
    return (
      <audio controls src={src} className="w-full">
        <track kind="captions" />
      </audio>
    );
  }
  if (kind === "video-direct") {
    return (
      <video controls src={src} className="w-full rounded-md border bg-black">
        <track kind="captions" />
      </video>
    );
  }
  return null;
}

function MediaCard({
  link,
  onDelete,
  onToggleShare,
  readOnly,
}: {
  link: MediaLinkRow;
  onDelete?: () => void;
  onToggleShare?: (next: boolean) => void;
  readOnly?: boolean;
}) {
  const Icon = iconFor(link.type);
  const embedKind = resolveEmbed(link.url, link.type).kind;
  const hasEmbed = embedKind !== "none";

  return (
    <div className="rounded-md border bg-card p-3 space-y-2">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium truncate">{link.label}</div>
          <a
            href={link.url}
            target="_blank"
            rel="noreferrer"
            className="text-[11px] text-muted-foreground hover:underline truncate block"
          >
            {link.url}
          </a>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-1 shrink-0">
            <label className="flex items-center gap-1 text-[10px] text-muted-foreground cursor-pointer pr-1">
              <input
                type="checkbox"
                checked={link.showOnShare}
                onChange={(e) => onToggleShare?.(e.target.checked)}
              />
              On share
            </label>
            <Button variant="ghost" size="sm" onClick={onDelete}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )}
      </div>
      {hasEmbed && <MediaEmbed link={link} />}
    </div>
  );
}

function AddMediaDialog({
  slug,
  open,
  onOpenChange,
}: {
  slug: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const labelInputId = useId();
  const urlInputId = useId();
  const [label, setLabel] = useState("");
  const [url, setUrl] = useState("");
  const [type, setType] = useState<MediaType>("audio");
  const [showOnShare, setShowOnShare] = useState(true);
  const create = useCreateMedia(slug);

  const submit = async () => {
    if (!label.trim() || !url.trim()) return;
    await create.mutateAsync({
      label: label.trim(),
      url: url.trim(),
      type,
      showOnShare,
    });
    setLabel("");
    setUrl("");
    setType("audio");
    setShowOnShare(true);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add media link</DialogTitle>
          <DialogDescription>
            Paste a URL — audio, video, mind map, or document. Audio and video links embed inline;
            others render as link cards.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor={labelInputId}>Label</Label>
            <Input
              id={labelInputId}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Audio overview"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={urlInputId}>URL</Label>
            <Input
              id={urlInputId}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Select value={type} onValueChange={(v) => setType(v as MediaType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={showOnShare}
              onChange={(e) => setShowOnShare(e.target.checked)}
            />
            Visible on shared links
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!label.trim() || !url.trim() || create.isPending}>
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function MediaPanel({ slug, className }: { slug: string; className?: string }) {
  const { data } = useMedia(slug);
  const update = useUpdateMedia(slug);
  const remove = useDeleteMedia(slug);
  const [addOpen, setAddOpen] = useState(false);

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between">
        <h3 className="text-base">Media</h3>
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          Add link
        </Button>
      </div>
      {data?.length === 0 && (
        <div className="rounded-md border border-dashed bg-muted/30 px-3 py-6 text-xs text-muted-foreground text-center">
          No media yet — audio overviews, video walkthroughs, and mind maps live here.
        </div>
      )}
      {data?.map((link) => (
        <MediaCard
          key={link.id}
          link={link}
          onDelete={() => remove.mutate(link.id)}
          onToggleShare={(next) => update.mutate({ mediaId: link.id, showOnShare: next })}
        />
      ))}
      <AddMediaDialog slug={slug} open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}

export function ReadOnlyMediaPanel({
  links,
  className,
}: {
  links: Array<Pick<MediaLinkRow, "id" | "label" | "url" | "type">>;
  className?: string;
}) {
  if (links.length === 0) return null;
  return (
    <div className={cn("space-y-3", className)}>
      <h3 className="text-base">Media</h3>
      {links.map((link) => {
        const Icon = iconFor(link.type as MediaType);
        const { kind } = resolveEmbed(link.url, link.type);
        const fullLink: MediaLinkRow = {
          id: link.id,
          solutionId: "",
          label: link.label,
          url: link.url,
          type: link.type as MediaType,
          showOnShare: true,
          sortOrder: 0,
          createdBy: "",
          createdAt: new Date().toISOString(),
        };
        return (
          <div key={link.id} className="rounded-md border bg-card p-3 space-y-2">
            <div className="flex items-center gap-2">
              <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{link.label}</div>
                <a
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-[11px] text-muted-foreground hover:underline truncate block"
                >
                  {link.url}
                </a>
              </div>
            </div>
            {kind !== "none" && <MediaEmbed link={fullLink} />}
          </div>
        );
      })}
    </div>
  );
}
